import { useState } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Divider } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PanToolIcon from '@mui/icons-material/PanTool';
import NotificationsIcon from "@mui/icons-material/Notifications";
import CategoryIcon from '@mui/icons-material/Category';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import MessageIcon from '@mui/icons-material/Message';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import BuildIcon from '@mui/icons-material/Build';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RadioIcon from '@mui/icons-material/Radio';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShieldIcon from '@mui/icons-material/Shield';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';

import {
    useTranslate,
    DashboardMenuItem,
    MenuItemLink,
    MenuProps,
    useSidebarState,
} from 'react-admin';

type MenuSection = 'userActivity' | 'contentMedia' | 'tools' | 'accessControl';

const Menu = ({ dense = false }: MenuProps) => {
    const [openSections, setOpenSections] = useState<Record<MenuSection, boolean>>({
        userActivity: true,
        contentMedia: true,
        tools: true,
        accessControl: true,
    });
    const translate = useTranslate();
    const [open] = useSidebarState();

    const handleToggle = (section: MenuSection) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const sectionHeaderStyle = {
        py: 0.5,
        minHeight: 40,
        '&:hover': {
            backgroundColor: 'action.hover',
        },
    };

    const menuItemStyle = {
        pl: open ? 4 : 2,
    };

    return (
        <Box
            sx={{
                width: open ? 220 : 50,
                marginTop: 1,
                marginBottom: 1,
                transition: theme =>
                    theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
            }}
        >
            <DashboardMenuItem />

            <Divider sx={{ my: 1 }} />

            {/* User Activity Section */}
            <List component="nav" disablePadding>
                <ListItemButton onClick={() => handleToggle('userActivity')} sx={sectionHeaderStyle}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    {open && (
                        <>
                            <ListItemText
                                primary="User Activity"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                            {openSections.userActivity ? <ExpandLess /> : <ExpandMore />}
                        </>
                    )}
                </ListItemButton>
                <Collapse in={openSections.userActivity} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <MenuItemLink
                            to="/likes"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.likes.name', { smart_count: 2 })}
                            leftIcon={<FavoriteIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/handsup"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.handsup.name', { smart_count: 2 })}
                            leftIcon={<PanToolIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                    </List>
                </Collapse>
            </List>

            {/* Content & Media Section */}
            <List component="nav" disablePadding>
                <ListItemButton onClick={() => handleToggle('contentMedia')} sx={sectionHeaderStyle}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <PermMediaIcon fontSize="small" />
                    </ListItemIcon>
                    {open && (
                        <>
                            <ListItemText
                                primary="Content & Media"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                            {openSections.contentMedia ? <ExpandLess /> : <ExpandMore />}
                        </>
                    )}
                </ListItemButton>
                <Collapse in={openSections.contentMedia} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <MenuItemLink
                            to="/categories"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('categories.name', { smart_count: 2 })}
                            leftIcon={<CategoryIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/channels"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.channels.name', { smart_count: 2 })}
                            leftIcon={<RadioIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/shows"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.shows.name', { smart_count: 2 })}
                            leftIcon={<VideoLibraryIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/messageTemplates"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.messageTemplates.name', { smart_count: 2 })}
                            leftIcon={<MessageIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                    </List>
                </Collapse>
            </List>

            {/* Tools Section */}
            <List component="nav" disablePadding>
                <ListItemButton onClick={() => handleToggle('tools')} sx={sectionHeaderStyle}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <BuildIcon fontSize="small" />
                    </ListItemIcon>
                    {open && (
                        <>
                            <ListItemText
                                primary="Tools"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                            {openSections.tools ? <ExpandLess /> : <ExpandMore />}
                        </>
                    )}
                </ListItemButton>
                <Collapse in={openSections.tools} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <MenuItemLink
                            to="/notifications"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.notifications.name', { smart_count: 2 })}
                            leftIcon={<NotificationsIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/votingEvents"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.voting.name', { smart_count: 2 })}
                            leftIcon={<HowToVoteIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/channelHealth"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.channelHealth.name', { smart_count: 2 })}
                            leftIcon={<MonitorHeartIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                        <MenuItemLink
                            to="/lineupGame"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.lineupGame.name', { smart_count: 2 })}
                            leftIcon={<SportsEsportsIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                    </List>
                </Collapse>
            </List>

            {/* Access Control Section */}
            <List component="nav" disablePadding>
                <ListItemButton onClick={() => handleToggle('accessControl')} sx={sectionHeaderStyle}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <ShieldIcon fontSize="small" />
                    </ListItemIcon>
                    {open && (
                        <>
                            <ListItemText
                                primary="Access Control"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                            {openSections.accessControl ? <ExpandLess /> : <ExpandMore />}
                        </>
                    )}
                </ListItemButton>
                <Collapse in={openSections.accessControl} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <MenuItemLink
                            to="/admins"
                            state={{ _scrollToTop: true }}
                            primaryText={translate('resources.admins.name', { smart_count: 2 })}
                            leftIcon={<AdminPanelSettingsIcon />}
                            dense={dense}
                            sx={menuItemStyle}
                        />
                    </List>
                </Collapse>
            </List>
        </Box>
    );
};

export default Menu;
